/* eslint-disable no-unused-vars */

import { cacheActions } from '@redux/slices/cache';
import SyncingService from '@services/inspection-work-order-checks-syncing.service';
import { getAppDispatch } from '@utilities/dispatch.util';

const syncRecordsAndTasks = async ({
  inspectionPendingSync,
  workOrdersPendingSync,
  pendingHseEvents,
  retrySync,
  syncFailedAssets,
  pendingChecks,
  pendingSyncLength,
  pendingEquipment
}) => {
  const dispatch = getAppDispatch?.();

  dispatch?.(cacheActions.updateSyncing(true));
  dispatch?.(cacheActions.updateSyncingModal(true));

  const pendingSyncInspectionLength = inspectionPendingSync?.length || 0;
  const pendingSyncWorkOrderLength = workOrdersPendingSync?.length || 0;
  const pendingSyncHSEEventsLength = pendingHseEvents?.length || 0;
  const pendingNewAndEditedEquipmentLength = pendingEquipment?.length || 0;

  // sync new and edited equipment assets
  await syncNewAndEditedEquipmentAssets({ pendingEquipment, retrySync, pendingSyncLength });

  // sync inspection assigned and new
  await syncAssignedAndNewInspection({
    inspectionPendingSync,
    retrySync,
    pendingSyncLength,
    pendingNewAndEditedEquipmentLength
  });

  // sync work order assigned and new
  await syncAssignedAndNewWorkOrder({
    workOrdersPendingSync,
    pendingSyncInspectionLength,
    pendingNewAndEditedEquipmentLength,
    retrySync,
    pendingSyncLength
  });

  // sync hse event new
  await syncNewHseEvents({
    pendingHseEvents,
    pendingSyncInspectionLength,
    pendingSyncWorkOrderLength,
    pendingNewAndEditedEquipmentLength,

    retrySync,
    pendingSyncLength
  });

  // sync checks assigned and new
  await syncAssignedAndNewChecks({
    pendingSyncInspectionLength,
    pendingSyncWorkOrderLength,
    pendingSyncHSEEventsLength,
    pendingNewAndEditedEquipmentLength,
    retrySync,
    pendingChecks,
    pendingSyncLength
  });

  if (syncFailedAssets) {
    await SyncingService.inspectionAndWorkOrderService({
      syncAssetsList: syncFailedAssets,
      currentTask: {},
      sync: false,
      doneNumber: null,
      pendingSyncLength: null,
      type: 'syncFailedAsset',
      retry: retrySync
    });
  }

  dispatch?.(cacheActions.setWorkOrderInspectionsAndChecksIsSync(true));
};

async function syncNewAndEditedEquipmentAssets({ retrySync, pendingSyncLength, pendingEquipment }) {
  for (let item in pendingEquipment) {
    const index = parseInt(item);
    const newRequest = pendingEquipment[index];

    const doneNumber = calculateDoneNumber({ index, syncAsset: true });
    await SyncingService.inspectionAndWorkOrderService({
      currentTask: newRequest,
      sync: false,
      doneNumber,
      pendingSyncLength,
      type: 'Equipment',
      retry: retrySync
    });
  }
}

async function syncAssignedAndNewInspection({
  inspectionPendingSync,
  retrySync,
  pendingSyncLength,
  pendingNewAndEditedEquipmentLength
}) {
  for (let item in inspectionPendingSync) {
    const index = parseInt(item);
    const newRequest = inspectionPendingSync[index];

    const doneNumber = calculateDoneNumber({
      index,
      syncInspection: true,
      pendingNewAndEditedEquipmentLength
    });

    await SyncingService.inspectionAndWorkOrderService({
      currentTask: newRequest,
      sync: false,
      doneNumber,
      pendingSyncLength,
      type: newRequest?.type === 'Inspection' ? 'Inspection' : 'Work Order',
      retry: retrySync
    });
  }
}

async function syncAssignedAndNewWorkOrder({
  workOrdersPendingSync,
  pendingSyncInspectionLength,
  retrySync,
  pendingSyncLength,
  pendingNewAndEditedEquipmentLength
}) {
  for (let item in workOrdersPendingSync) {
    const index = parseInt(item);

    const newRequest = workOrdersPendingSync[index];

    const doneNumber = calculateDoneNumber({
      index,
      syncWorkOrder: true,
      pendingNewAndEditedEquipmentLength,
      pendingSyncInspectionLength
    });

    await SyncingService.inspectionAndWorkOrderService({
      currentTask: newRequest,
      sync: false,
      doneNumber: doneNumber,
      pendingSyncLength,
      type: newRequest?.type === 'Inspection' ? 'Inspection' : 'Work Order',
      retry: retrySync
    });
  }
}

async function syncNewHseEvents({
  pendingHseEvents,
  pendingSyncInspectionLength,
  pendingSyncWorkOrderLength,
  retrySync,
  pendingSyncLength,
  pendingNewAndEditedEquipmentLength
}) {
  for (let item in pendingHseEvents) {
    const index = parseInt(item);
    const newHse = pendingHseEvents[index];

    const doneNumber = calculateDoneNumber({
      index,
      syncHSE: true,
      pendingNewAndEditedEquipmentLength,
      pendingSyncInspectionLength,
      pendingSyncWorkOrderLength
    });

    await SyncingService.inspectionAndWorkOrderService({
      currentTask: newHse,
      sync: false,
      doneNumber,
      pendingSyncLength,
      type: 'HSE',
      retry: retrySync
    });
  }
}

async function syncAssignedAndNewChecks({
  pendingSyncInspectionLength,
  pendingSyncWorkOrderLength,
  pendingSyncHSEEventsLength,
  retrySync,
  pendingChecks,
  pendingSyncLength,
  pendingNewAndEditedEquipmentLength
}) {
  for (let item in pendingChecks) {
    const index = parseInt(item);
    const newCheck = pendingChecks[index];

    const doneNumber = calculateDoneNumber({
      index,
      syncCheck: true,
      pendingNewAndEditedEquipmentLength,
      pendingSyncInspectionLength,
      pendingSyncWorkOrderLength,
      pendingSyncHSEEventsLength
    });

    await SyncingService.inspectionAndWorkOrderService({
      currentTask: newCheck,
      sync: false,
      doneNumber,
      pendingSyncLength,
      type: 'Check',
      retry: retrySync
    });
  }
}

export { syncRecordsAndTasks };

const calculateDoneNumber = ({
  pendingSyncInspectionLength = 0,
  pendingSyncWorkOrderLength = 0,
  pendingSyncHSEEventsLength = 0,
  pendingNewAndEditedEquipmentLength = 0,

  syncAsset = false,
  syncInspection = false,
  syncWorkOrder = false,
  syncHSE = false,
  syncCheck = false,
  index = 0
}) => {
  const steps = [
    { key: 'asset', active: syncAsset, count: 0 },
    { key: 'inspection', active: syncInspection, count: pendingNewAndEditedEquipmentLength },
    {
      key: 'workOrder',
      active: syncWorkOrder,
      count: pendingSyncInspectionLength + pendingNewAndEditedEquipmentLength
    },
    {
      key: 'hse',
      active: syncHSE,
      count:
        pendingSyncInspectionLength +
        pendingSyncWorkOrderLength +
        pendingNewAndEditedEquipmentLength
    },
    {
      key: 'check',
      active: syncCheck,
      count:
        pendingSyncInspectionLength +
        pendingSyncWorkOrderLength +
        pendingSyncHSEEventsLength +
        pendingNewAndEditedEquipmentLength
    }
  ];

  for (const step of steps) {
    if (step.active) {
      return step.count + index + 1;
    }
  }

  return index + 1;
};
